/**
 * CORE-1102 — a join that never settles.
 *
 * QA reported «Live падает на инициализации Agora» on prod (25.08). What prod
 * telemetry actually shows is worse than a crash: the join simply never comes
 * back. Three sessions, one after the other:
 *
 *   15:48:54.332  room_rtc.connecting
 *   15:48:55.230  AgoraRTCError GET_LOCAL_CONNECTION_PARAMS_FAILED: Invalid space at 12
 *   15:48:55.889  …the same, again
 *   15:49:07.269  room_rtc.join_failed — WS_ABORT: LEAVE
 *
 * The SDK gives up on its own SDP inside a second, and then `client.join()`
 * neither resolves nor rejects. The last row is not the failure — it is the
 * user walking out 12 s later, our `cleanup()` calling `client.leave()`, and
 * Agora finally rejecting the abandoned join. Until then the screen sits on
 * «Подключаемся…» with no error and no way back, because the error panel
 * (`state === 'error'`, with «Повторить») is only ever reached by a rejection.
 *
 * The root cause is inside the SDK — its own strict SDP grammar
 * (`consumeAttributeSpace`) refusing an `ice-options` value — and it is NOT
 * fixed upstream: `parseIceOptions` is byte-identical in 4.24.2 (ours) and
 * 4.24.7 (latest). So the SDK cannot be trusted to always settle, and the
 * caller needs a deadline of its own.
 *
 * 20 s is deliberately generous. Every one of the 22 successful joins measured
 * on prod over 24–25.08 (`connecting` → `joined`, which includes the token
 * fetch) landed within **4.5 s**, median 1.7 s. The deadline is over four
 * times the worst real join and still far under the 15–45 s those users spent
 * staring at a spinner.
 */

/** Over four times the slowest join measured on prod. See the note above. */
export const RTC_JOIN_TIMEOUT_MS = 20_000;

export const RTC_JOIN_TIMEOUT_CODE = 'JOIN_TIMEOUT';

/** Carries a `code` so it groups in telemetry beside the SDK's own errors. */
export class RtcJoinTimeoutError extends Error {
  readonly code = RTC_JOIN_TIMEOUT_CODE;
  readonly timeoutMs: number;

  constructor(timeoutMs: number) {
    super(`RTC join did not settle within ${timeoutMs} ms`);
    this.name = 'RtcJoinTimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export const isRtcJoinTimeout = (err: unknown): err is RtcJoinTimeoutError =>
  !!err && (err as any).code === RTC_JOIN_TIMEOUT_CODE;

/**
 * Rejects with {@link RtcJoinTimeoutError} if `join` has not settled in
 * `timeoutMs`. A non-positive timeout disables the deadline entirely, which is
 * how a test asks for the old unbounded behaviour.
 */
export function withJoinDeadline<T>(join: Promise<T>, timeoutMs: number = RTC_JOIN_TIMEOUT_MS): Promise<T> {
  if (!(timeoutMs > 0)) return join;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let timedOut = false;

  // The losing promise stays alive: the abandoned join does eventually reject
  // (WS_ABORT: LEAVE, once the screen tears the client down), and by then the
  // race has no handler left for it. Swallow it here, or the global
  // unhandled-rejection reporter files it as a second, phantom failure.
  const guarded = join.catch((err) => {
    if (timedOut) return undefined as unknown as T;
    throw err;
  });

  const deadline = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => {
      timedOut = true;
      reject(new RtcJoinTimeoutError(timeoutMs));
    }, timeoutMs);
  });

  return Promise.race([guarded, deadline]).finally(() => {
    if (timer !== null) clearTimeout(timer);
  });
}
